import presentation from '../constants/studioPresentation.json';
import React, { useEffect, useState, useContext } from 'react';
import styles from './ClientPage.module.css';
import { UserContext } from '../contexts/UserContext';
import { checkTokenAndLogout } from '../utils/auth';
import { initVersionCheck } from '../../utils/versionCheck';
import { getThemeForPage } from '../styles/themes';
import { getPageContainerStyles } from '../utils/styleUtils';

import ClientMemoList from '../components/ClientMemoList';
import ClientMemoModal from '../components/ClientMemoModal';
import GlitchRainText from '../components/GlitchRainText';
import WeatherLine from '../components/WeatherLine';
import ReservationSummary from '../components/ReservationSummary';
import AnnouncementPanel from '../components/AnnouncementPanel';

function getLocale() {
  const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('ko')) return 'ko';
  if (lang.startsWith('en')) return 'en';
  return 'ko'; // 기본값을 한국어로 설정
}

// 다국어 텍스트 - 현재 미사용
// eslint-disable-next-line no-unused-vars
const TEXT = {
  writeMemo: {
    fr: 'Écrire un mémo',
    ko: '메모 작성',
    en: 'Write Memo',
  },
  cacheCleared: {
    fr: 'Le cache a été effacé avec succès.',
    ko: '캐시가 성공적으로 지워졌습니다.',
    en: 'Cache has been cleared successfully.',
  },
  cacheClearError: {
    fr: 'Une erreur s\'est produite lors de l\'effacement du cache.',
    ko: '캐시 지우기 중 오류가 발생했습니다.',
    en: 'An error occurred while clearing the cache.',
  }
};

function ClientPage() {
  const { userInfo } = useContext(UserContext);
  // eslint-disable-next-line no-unused-vars
  const locale = userInfo?.preferred_language || getLocale();
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [memoListKey, setMemoListKey] = useState(0);

  // 현재 테마 가져오기
  const currentTheme = getThemeForPage('/client');
  const pageContainerStyles = getPageContainerStyles();


  // 컴포넌트 마운트 시 토큰 검사 및 지속적 모니터링
  useEffect(() => {
    // 초기 토큰 검사
    if (checkTokenAndLogout()) {
      return;
    }

    // 모바일 환경을 위한 더 자주 토큰 체크 (10초마다)
    const tokenCheckInterval = setInterval(() => {
      if (checkTokenAndLogout()) {
        clearInterval(tokenCheckInterval);
        return;
      }
    }, 10000); // 10초마다 체크

    // 페이지 포커스 시 토큰 체크 (모바일에서 앱 전환 시)
    const handleFocus = () => {
      if (checkTokenAndLogout()) {
        clearInterval(tokenCheckInterval);
        return;
      }
    };

    // 페이지 가시성 변경 시 토큰 체크 (모바일에서 백그라운드/포그라운드 전환 시)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (checkTokenAndLogout()) {
          clearInterval(tokenCheckInterval);
          return;
        }
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 정리 함수
    return () => {
      clearInterval(tokenCheckInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // ClientPage에서만 버전 체크 실행
  useEffect(() => {
    console.log('ClientPage: Initializing version check');
    initVersionCheck();
  }, []);

  // 테마에 맞게 PWA 노치 여백 배경색 설정
  useEffect(() => {
    // theme-color 메타 태그 업데이트
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', currentTheme.backgroundColor);
    }

    // body 배경색도 테마에 맞게 변경 (PWA 노치 여백 포함)
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = currentTheme.backgroundColor;

    // 정리 함수: 페이지를 벗어날 때 기본값으로 복원
    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8'); // 기본 검은색으로 복원
      }
      document.body.style.backgroundColor = originalBodyBg; // 원래 배경색으로 복원
    };
  }, [currentTheme.backgroundColor]);

  const handleMemoUpdate = () => {
    setMemoListKey(prev => prev + 1);
  };

  // 토큰 만료 체크 (컴포넌트 렌더링 전)
  if (checkTokenAndLogout()) {
    return <div style={{
      padding: '3px',
      textAlign: 'center',
      color: currentTheme.inactiveColor
    }}>Redirecting...</div>;
  }

  if (!userInfo?.user_id) {
    return <div style={{
      padding: '3px',
      textAlign: 'center',
      color: currentTheme.inactiveColor
    }}>Loading user info...</div>;
  }

  return (
    <div
      className={`${pageContainerStyles.pageScrollContainer} ${pageContainerStyles.pageScrollSafeArea} ${styles.wrapper}`}
      style={{
        '--page-scroll-bottom-offset': '80px',
        backgroundColor: currentTheme.backgroundColor,
      }}
    >
      {/* 테마 컨테이너 */}
      <div style={{
        width: '80vw',
        margin: '10px auto 0 auto',
        backgroundColor: 'transparent',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: 'none',
        border: 'none',
        color: currentTheme.inactiveColor
      }}>
        {/* Section 1 - 글리치 텍스트 애니메이션 */}
        <div style={{ marginTop: '0' }}>
          <GlitchRainText text="GNEGNEGNE" theme={currentTheme} />
        </div>

        {/* Section 2 - 날씨, 주소, 날짜 */}
        <div style={{ marginTop: 'var(--space-section)' }}>
          <WeatherLine />
          <div style={{
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: '300',
            color: '#999',
            letterSpacing: '0.5px',
            marginTop: '8px'
          }}>
            {presentation.address}<br />
            {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')}
          </div>
        </div>

        {/* Section 3 - 예약 현황 */}
        <div style={{ marginTop: 'var(--space-section)' }}>
          <ReservationSummary />
        </div>

        {/* Section 4 - 공지 */}
        <AnnouncementPanel currentTheme={currentTheme} />

        {/* Section 5 - 메모 섹션 */}
        <div style={{ marginTop: 'var(--space-section)', padding: '0' }}>
          <ClientMemoList
            key={memoListKey}
            userInfo={userInfo}
            onMemoUpdate={handleMemoUpdate}
            onShowModal={() => setShowMemoModal(true)}
            currentTheme={currentTheme}
          />
        </div>

      </div>

      {/* 메모 작성 모달 */}
      <ClientMemoModal
        isOpen={showMemoModal}
        onClose={() => setShowMemoModal(false)}
        onMemoAdded={handleMemoUpdate}
        userInfo={userInfo}
        currentTheme={currentTheme}
      />
    </div>
  );
}

export default ClientPage;
